import React, { useEffect, useRef, useState } from 'react';

import { errorMessage } from '../../services/apiError';
import { loopService } from '../../services/loopService';
import type { LoopAttachment } from '../../services/loopService';

import './LoopAttachments.css';

const MAX_FILES = 10;
const MAX_BYTES = 10 * 1024 * 1024;
const EMPTY_ATTACHMENTS: LoopAttachment[] = [];

// Local additions/removals remain visible until the next server poll catches up.
// eslint-disable-next-line max-lines-per-function, max-statements
export const useLoopAttachments = (
  loopId?: string, saved: LoopAttachment[] = EMPTY_ATTACHMENTS, initialIds: string[] = [],
  onChanged?: () => Promise<void>, ownerKey?: string,
) => {
  const [added, setAdded] = useState<LoopAttachment[]>([]);
  const [removed, setRemoved] = useState<string[]>([]);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [unresolvedIds, setUnresolvedIds] = useState(initialIds);
  const working = useRef(false);
  const generation = useRef(0);
  const [owner, setOwner] = useState(ownerKey);

  if (owner !== ownerKey) {
    setOwner(ownerKey); setAdded([]); setRemoved([]); setUnresolvedIds([]); setError(null); setPending(false);
  }
  const initialKey = initialIds.join(',');

  // Once the server acknowledges an upload, future server removals must win.
  if (loopId && added.some((file) => saved.some((item) => item.id === file.id))) {
    setAdded(added.filter((file) => !saved.some((item) => item.id === file.id)));
  }
  const attachments = Array.from(new Map([...saved, ...added].map((file) => [file.id, file])).values())
    .filter((file) => !removed.includes(file.id));
  const attachmentIds = Array.from(new Set([...attachments.map((file) => file.id), ...unresolvedIds]))
    .filter((id) => !removed.includes(id));

  useEffect(() => {
    working.current = false;

    return () => { generation.current += 1; };
  }, [ownerKey]);

  useEffect(() => {
    if (!initialKey) return;
    let active = true;

    void loopService.attachmentMetadata(initialKey.split(',')).then((files) => {
      if (!active) return;
      setAdded((items) => Array.from(new Map([...items, ...files].map((file) => [file.id, file])).values()));
      setUnresolvedIds([]);
    }).catch((reason) => { if (active) setError(errorMessage(reason, 'Your attached files could not be loaded. They are still included.')); });

    return () => { active = false; };
  }, [initialKey, ownerKey]);

  // eslint-disable-next-line max-statements
  const upload = async (files: File[]) => {
    if (working.current || !files.length) return;
    if (files.length + attachmentIds.length > MAX_FILES) { setError('Attach up to 10 files per loop.');

      return; }
    const started = generation.current;

    working.current = true; setPending(true); setError(null);
    const failures: string[] = [];

    for (const file of files) {
      if (started !== generation.current) return;
      if (file.size > MAX_BYTES) { failures.push(`${file.name}: maximum file size is 10 MB.`); continue; }
      try {
        const attached = await loopService.uploadAttachment(file, loopId);

        if (started !== generation.current) return;
        setAdded((items) => [...items, attached]);
      } catch (reason) { failures.push(`${file.name}: ${errorMessage(reason, 'Upload failed. Choose the file again to retry.')}`); }
    }
    if (started !== generation.current) return;
    if (failures.length) setError(failures.join(' '));
    try { if (loopId) await onChanged?.(); }
    catch { setError('Your files were saved, but the loop could not refresh.'); }
    finally { if (started === generation.current) { working.current = false; setPending(false); } }
  };
  // eslint-disable-next-line max-statements
  const remove = async (id: string) => {
    if (working.current) return;
    const started = generation.current;

    working.current = true; setPending(true); setError(null);
    try {
      await loopService.removeAttachment(id);
      if (started !== generation.current) return;
      setRemoved((items) => [...items, id]);
      if (loopId) await onChanged?.();
    } catch (reason) {
      if (started === generation.current) setError(errorMessage(reason, 'The file could not be removed. Please try again.'));
    } finally { if (started === generation.current) { working.current = false; setPending(false); } }
  };
  // eslint-disable-next-line max-statements
  const download = async (file: LoopAttachment) => {
    const started = generation.current;

    try {
      const data = await loopService.downloadAttachment(file.id);

      if (started !== generation.current) return;
      const url = URL.createObjectURL(data);
      const link = document.createElement('a');

      link.href = url; link.download = file.name; link.click();
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (reason) {
      if (started === generation.current) setError(errorMessage(reason, 'The file could not be downloaded.'));
    }
  };

  return { attachments, attachmentIds, unresolvedIds, pending, error, upload, remove, download,
    removeUnresolved: () => { setRemoved((items) => [...items, ...unresolvedIds]); setUnresolvedIds([]); },
    clearError: () => setError(null) };
};
export type LoopAttachmentsState = ReturnType<typeof useLoopAttachments>;

export const LoopAttachmentButton: React.FC<{
  state: LoopAttachmentsState; disabled?: boolean; onSignIn?: () => void;
}> = ({ state, disabled, onSignIn }) => {
  const input = useRef<HTMLInputElement>(null);

  return <>
    <button className="loop-attach-button" type="button" title="Attach files (up to 10 MB each)"
      aria-label="Attach files to loop" disabled={disabled || state.pending || state.attachmentIds.length >= MAX_FILES}
      onClick={() => { if (onSignIn) onSignIn(); else input.current?.click(); }}>
      <i className="fa-solid fa-paperclip" aria-hidden="true" />
    </button>
    <input ref={input} type="file" multiple hidden aria-label="Choose Loop attachments"
      onChange={(event) => { void state.upload(Array.from(event.target.files || [])); event.target.value = ''; }} />
  </>;
};

export const LoopAttachmentList: React.FC<{ state: LoopAttachmentsState; disabled?: boolean }> = (
  { state, disabled },
) => (
  <div className="loop-attachments" aria-label="Loop attachments">
    {!!state.attachments.length && <ul>{state.attachments.map((file) => <li key={file.id}>
      <button type="button" className="loop-attachment-file"
        title={`${file.name} · ${Math.max(1, Math.ceil(file.size_bytes / 1024))} KB`}
        onClick={() => void state.download(file)}><i className="fa-regular fa-file" aria-hidden="true" />
        <span>{file.name}</span></button>
      <button type="button" className="loop-attachment-remove" aria-label={`Remove ${file.name}`}
        disabled={disabled || state.pending} onClick={() => void state.remove(file.id)}>
        <i className="fa-solid fa-xmark" aria-hidden="true" /></button>
    </li>)}</ul>}
    {!!state.unresolvedIds.length && <p role="status">{state.unresolvedIds.length} attached file(s) included.
      <button type="button" className="loop-attachment-remove" onClick={state.removeUnresolved}
        disabled={disabled || state.pending}>Remove</button></p>}
    {state.pending && <p role="status"><i className="fa-solid fa-spinner fa-spin" aria-hidden="true" /> Updating attachments…</p>}
    {state.error && <p className="loop-attachments__error" role="alert">{state.error}
      <button type="button" aria-label="Dismiss attachment error" onClick={state.clearError}>×</button></p>}
  </div>
);
